import React from 'react';
import { IconArrowDownRight, IconArrowUpRight, IconMinus } from '@tabler/icons-react';
import styles from './Detail.module.scss';
import StatusBadge from './StatusBadge';
import InlineBar from './InlineBar';
import type { Direction, Tone } from '../detail';

export interface TileDelta {
  text: string;
  direction: Direction;
  /** Whether up is good for this KPI; decides the colour, never the arrow. */
  higherIsBetter: boolean;
  /** "vs. 4 weeks ago", "since 12.09." */
  hint: string;
}

interface MetricTileProps {
  label: string;
  value: string;
  /** A second, smaller value line, e.g. "of 42" or "5 of 7 phases". */
  sub?: string;
  delta?: TileDelta;
  status?: { tone: Tone; label: string };
  /** A bar under the value - the share this tile is about. */
  bar?: { value?: number; tone?: Tone };
  footer?: React.ReactNode;
  /** Tooltip explaining what is counted. */
  title?: string;
  onClick?: () => void;
  active?: boolean;
}

/**
 * One headline number of a Detailmasken page: value, its change against the
 * comparison point, and its status. Clickable when it leads somewhere - the KPI
 * side panel in the Cockpit, a filtered list on a report page.
 */
const MetricTile: React.FC<MetricTileProps> = ({
  label,
  value,
  sub,
  delta,
  status,
  bar,
  footer,
  title,
  onClick,
  active,
}) => {
  const DeltaIcon =
    delta?.direction === 'up' ? IconArrowUpRight : delta?.direction === 'down' ? IconArrowDownRight : IconMinus;
  const deltaClass =
    !delta || delta.direction === 'flat'
      ? styles.flat
      : (delta.direction === 'up') === delta.higherIsBetter
        ? styles.good
        : styles.bad;

  const content = (
    <>
      <span className={styles.tileLabel}>{label}</span>
      <div className={styles.tileValueRow}>
        <span className={styles.tileValue}>{value}</span>
        {delta && (
          <span className={`${styles.delta} ${deltaClass}`}>
            <span>
              <DeltaIcon size={13} stroke={2.2} /> {delta.text}
            </span>
            <span className={styles.deltaHint}>{delta.hint}</span>
          </span>
        )}
        {sub && <span className={styles.tileSub}>{sub}</span>}
      </div>
      {bar && <InlineBar value={bar.value} tone={bar.tone} hideValue />}
      {(status || footer) && (
        <div className={styles.tileFooter}>
          {status ? <StatusBadge tone={status.tone} label={status.label} /> : <span />}
          {footer}
        </div>
      )}
    </>
  );

  return onClick ? (
    <button
      type="button"
      className={`${styles.tile} ${active ? styles.tileActive : ''}`}
      onClick={onClick}
      title={title}
      aria-pressed={active}
    >
      {content}
    </button>
  ) : (
    <div className={styles.tile} title={title}>
      {content}
    </div>
  );
};

export default MetricTile;
